import React, { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Building2, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

export default function CompanySwitcher() {
  const { user, isSuperAdmin, activeCompanyId, switchCompany } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get('/companies/');
        if (!cancelled) {
          setCompanies(data);
          const a = data.find((c) => c.id === (activeCompanyId || user?.company_id)) || (isSuperAdmin ? null : data[0]);
          setActive(a);
        }
      } catch {}
    }
    if (user) load();
    return () => { cancelled = true; };
  }, [user, activeCompanyId, isSuperAdmin]);

  if (!user) return null;

  // Non-super admin: just show their company name
  if (!isSuperAdmin) {
    return (
      <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md border border-[var(--divider)] bg-white" data-testid="current-company-display">
        {user.company_logo ? (
          <img src={user.company_logo} alt="" className="w-5 h-5 rounded object-cover" />
        ) : (
          <Building2 size={14} className="text-slate-500" />
        )}
        <span className="text-sm text-slate-800">{user.company_name || 'No Company'}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="company-switcher"
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-[var(--divider)] bg-white hover:bg-slate-50 transition-colors"
        >
          {active?.logo_data_url ? (
            <img src={active.logo_data_url} alt="" className="w-5 h-5 rounded object-cover" />
          ) : (
            <Building2 size={14} className="text-slate-500" />
          )}
          <span className="text-sm text-slate-800 max-w-[180px] truncate">
            {active ? active.name : 'All Companies'}
          </span>
          <ChevronDown size={14} className="text-slate-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs uppercase tracking-wide text-slate-500">Switch Company</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => { switchCompany(null); setActive(null); }}
          data-testid="company-switcher-item-all"
        >
          <Building2 size={14} className="mr-2" />
          <span className="flex-1">All Companies (system-wide)</span>
          {!activeCompanyId && <Check size={14} />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {companies.map((c) => (
          <DropdownMenuItem
            key={c.id}
            onClick={() => { switchCompany(c.id); setActive(c); }}
            data-testid={`company-switcher-item-${c.id}`}
          >
            {c.logo_data_url ? (
              <img src={c.logo_data_url} alt="" className="w-5 h-5 rounded object-cover mr-2" />
            ) : (
              <Building2 size={14} className="mr-2" />
            )}
            <span className="flex-1 truncate">{c.name}</span>
            {activeCompanyId === c.id && <Check size={14} />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
